const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new Error(
      response.status === 401
        ? 'This policy reference requires an authenticated CARES session.'
        : response.status === 403
          ? 'You do not have access to the requested request policy scope.'
          : response.status === 422
            ? 'The selected request policy scope is not available.'
            : `Request policy call failed (${response.status}).`
    );
  }

  return response.json() as Promise<T>;
};

export type RequestPolicyGroup = {
  id: number;
  slug: string;
  name: string;
  company: {
    id: number | null;
    slug: string | null;
    name: string | null;
  };
  isSelected: boolean;
};

export type RequestPolicyResolvedScope = {
  scopeType: string;
  companyId: number | null;
  companySlug: string | null;
  companyName: string | null;
  groupId: number | null;
  groupSlug: string | null;
  groupName: string | null;
} | null;

export type RequestPolicyCompleteness = {
  status: 'complete' | 'partial' | 'unavailable';
  conservativeActions: boolean;
  availableSources: string[];
  missingSources: string[];
  fallbackSource: string | null;
  message: string;
};

export type RequestPolicyMetadata = {
  status: string;
  provenance: string;
  fallbackSource: string | null;
  resolvedAt: string;
};

export type RequestPolicyAction = {
  allowed: boolean;
  status: string;
  reasonCode: string;
  message: string;
};

export type RequestPolicyItem = {
  key: string;
  title: string;
  status: string;
  summary: string;
  detail: string;
  provenance: string;
  effectiveDate?: string | null;
};

export type RequestPolicyResponse = {
  resolvedScope: RequestPolicyResolvedScope;
  availableGroups: RequestPolicyGroup[];
  dataCompleteness: RequestPolicyCompleteness;
  policy: RequestPolicyMetadata;
  actionPermissions: {
    submit: RequestPolicyAction;
    modify: RequestPolicyAction;
    delete: RequestPolicyAction;
  };
  currentPolicy: RequestPolicyItem[];
  futurePolicy: RequestPolicyItem[];
};

type RawRequestPolicyGroup = {
  id: number;
  slug: string;
  name: string;
  company: {
    id: number | null;
    slug: string | null;
    name: string | null;
  };
  is_selected: boolean;
};

type RawRequestPolicyResolvedScope = {
  scope_type: string;
  company_id: number | null;
  company_slug: string | null;
  company_name: string | null;
  group_id: number | null;
  group_slug: string | null;
  group_name: string | null;
} | null;

type RawRequestPolicyCompleteness = {
  status: 'complete' | 'partial' | 'unavailable';
  conservative_actions: boolean;
  available_sources: string[];
  missing_sources: string[];
  fallback_source: string | null;
  message: string;
};

type RawRequestPolicyMetadata = {
  status: string;
  provenance: string;
  fallback_source: string | null;
  resolved_at: string;
};

type RawRequestPolicyAction = {
  allowed: boolean;
  status: string;
  reason_code: string;
  message: string;
};

type RawRequestPolicyItem = {
  key: string;
  title: string;
  status: string;
  summary: string;
  detail: string;
  provenance: string;
  effective_date?: string | null;
};

type RawRequestPolicyResponse = {
  resolved_scope: RawRequestPolicyResolvedScope;
  available_groups: RawRequestPolicyGroup[];
  data_completeness: RawRequestPolicyCompleteness;
  policy: RawRequestPolicyMetadata;
  action_permissions: {
    submit: RawRequestPolicyAction;
    modify: RawRequestPolicyAction;
    delete: RawRequestPolicyAction;
  };
  current_policy: RawRequestPolicyItem[];
  future_policy: RawRequestPolicyItem[];
};

const mapGroup = (group: RawRequestPolicyGroup): RequestPolicyGroup => ({
  id: group.id,
  slug: group.slug,
  name: group.name,
  company: group.company,
  isSelected: group.is_selected,
});

const mapScope = (scope: RawRequestPolicyResolvedScope): RequestPolicyResolvedScope => (scope ? {
  scopeType: scope.scope_type,
  companyId: scope.company_id,
  companySlug: scope.company_slug,
  companyName: scope.company_name,
  groupId: scope.group_id,
  groupSlug: scope.group_slug,
  groupName: scope.group_name,
} : null);

const mapItem = (item: RawRequestPolicyItem): RequestPolicyItem => ({
  key: item.key,
  title: item.title,
  status: item.status,
  summary: item.summary,
  detail: item.detail,
  provenance: item.provenance,
  effectiveDate: item.effective_date ?? null,
});

export const fetchMyScheduleRequestPolicy = async (group?: string): Promise<RequestPolicyResponse> => {
  const params = new URLSearchParams();
  if (group) params.set('group', group);

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const payload = await readJson<RawRequestPolicyResponse>(
    await fetch(`/api/v1/secure/my-schedule/request-policy${suffix}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return {
    resolvedScope: mapScope(payload.resolved_scope),
    availableGroups: payload.available_groups.map(mapGroup),
    dataCompleteness: {
      status: payload.data_completeness.status,
      conservativeActions: payload.data_completeness.conservative_actions,
      availableSources: payload.data_completeness.available_sources,
      missingSources: payload.data_completeness.missing_sources,
      fallbackSource: payload.data_completeness.fallback_source,
      message: payload.data_completeness.message,
    },
    policy: {
      status: payload.policy.status,
      provenance: payload.policy.provenance,
      fallbackSource: payload.policy.fallback_source,
      resolvedAt: payload.policy.resolved_at,
    },
    actionPermissions: {
      submit: payload.action_permissions.submit,
      modify: payload.action_permissions.modify,
      delete: payload.action_permissions.delete,
    },
    currentPolicy: payload.current_policy.map(mapItem),
    futurePolicy: payload.future_policy.map(mapItem),
  };
};
